import React, { useState } from 'react';
import { unformat } from 'accounting';
import classNames from 'classnames';

import { useComponentDidUpdate, usePrevious } from '@hcs/hooks';
import {
  capitalizeFirstLetter,
  formatMoney,
  formatNumber,
  isNumberOrDecimalPoint,
  isPositive,
} from '@hcs/utils';

import { Input } from '../../inputs';

import { FilterRangeType, FormatTypes, RangeTypes } from './FilterRange.types';

import styles from './FilterRange.module.css';

const undefinedNullOrEmptyString = (value?: string | null): boolean => {
  return value === undefined || value === null || value.length === 0;
};

const formatDecimalNumber = (value: string | null) => {
  return `${Number(value)?.toFixed(2)}`;
};

const formatMoneyStr = (
  value: string,
  options?: accounting.NumberSettings,
): string => {
  return formatMoney(Number(value), options);
};

const formatOrUndefined = (
  formatter: (value: string, options?: accounting.NumberSettings) => string,
  value: FilterRangeType,
  options?: accounting.NumberSettings,
): FilterRangeType => {
  return {
    min: undefinedNullOrEmptyString(value?.min)
      ? undefined
      : formatter(value?.min || '', options),
    max: undefinedNullOrEmptyString(value?.max)
      ? undefined
      : formatter(value?.max || '', options),
  };
};

const formatValue = (
  value: FilterRangeType,
  decimal: boolean,
  formatType: FormatTypes,
): FilterRangeType => {
  const cleanValue: FilterRangeType = formatOrUndefined(
    formatStripFormatting,
    value,
  );
  const options = decimal ? { precision: 2 } : { precision: 0 };
  switch (formatType) {
    case 'COMMA':
      return formatOrUndefined(
        //Simple wrapper for formatNumber to fulfill formatter contract
        (value?: string, options?: accounting.NumberSettings): string => {
          return formatNumber(Number(value), options);
        },
        cleanValue,
        options,
      );
    case 'DECIMAL_NUMBER':
      return formatOrUndefined(formatDecimalNumber, cleanValue);
    case 'MONEY':
      return formatOrUndefined(formatMoneyStr, cleanValue, options);
    default: {
      // number type return as is
      return value;
    }
  }
};

const validateRange = (value: FilterRangeType): string | undefined => {
  const types: RangeTypes[] = ['max', 'min'];
  for (const rangeType of types) {
    if (value[rangeType] && isNaN(Number(value[rangeType])))
      return `${capitalizeFirstLetter(rangeType)} must be a number`;
    if (value[rangeType] && !isPositive(Number(value[rangeType])))
      return `${capitalizeFirstLetter(rangeType)} must be positive`;
  }
  if (Number(value.min) > Number(value.max)) {
    return 'There is a problem with this filter! The minimum value is greater than the maximum value.';
  }

  return undefined;
};

export interface FilterRangeProps {
  initialValue?: FilterRangeType;
  subjectDisplay?: FilterRangeType;
  className?: string;
  decimal?: boolean;
  placeHolders?: {
    min?: string;
    max?: string;
  };
  hideLabels?: boolean;
  maxVal?: number;
  minVal?: number;
  dataHcName: string;
  formatType?: FormatTypes;
  onBlur?: (value: FilterRangeType) => void;
  extraValidators?: ((v: FilterRangeType) => string | undefined)[];
  disabled?: boolean;
  showPercentSymbol?: boolean;
}

const formatStripFormatting = (value: string) =>
  value.replace(/(sqft\.|m|k|\$|,)/gi, '').trim();

const defaultValidators = [validateRange];

export const FilterRange = ({
  initialValue = { min: undefined, max: undefined },
  onBlur,
  className,
  maxVal,
  minVal,
  decimal = false,
  formatType = 'NUMBER',
  extraValidators = [],
  subjectDisplay,
  dataHcName,
  placeHolders = {
    min: 'No Min',
    max: 'No Max',
  },
  hideLabels = true,
  disabled,
  showPercentSymbol,
}: FilterRangeProps) => {
  const formattedValue = formatValue(initialValue, decimal, formatType);
  const [value, setValue] = useState<FilterRangeType>(formattedValue);
  const prevInitialValue = usePrevious<FilterRangeType>(formattedValue);

  const [error, setError] = useState<string>('');
  const validators = [...defaultValidators, ...extraValidators];

  useComponentDidUpdate(() => {
    // If we pass in a new initial value then update
    if (
      JSON.stringify(value) !== JSON.stringify(initialValue) &&
      JSON.stringify(value) === JSON.stringify(prevInitialValue) // make sure parent has been updated before setting new value
    ) {
      setValue(formatValue(initialValue, decimal, formatType));
    }
  }, [initialValue]);

  const checkValidity = (): boolean => {
    const isValid = validators.every((validator) => {
      const validationResult = validator(
        formatOrUndefined(formatStripFormatting, value),
      );
      if (validationResult) {
        setError(validationResult);
      }
      return !validationResult;
    });
    if (isValid) setError('');
    return isValid;
  };

  const handleChange = (newValue: FilterRangeType) => {
    // If the last character is a comma it must have been typed
    // so we reject it, we only want to have comma's in the formatted value
    if (
      ('min' in newValue && newValue.min?.slice(-1) === ',') ||
      newValue.max?.slice(-1) === ','
    ) {
      return;
    }

    const currentVal = { ...value, ...newValue };
    const primVal = 'min' in newValue ? newValue.min : newValue.max;

    if (
      primVal === undefined ||
      primVal.length === 0 ||
      // if decimal numbers are allowed allow 1 decimal point else 0
      (primVal &&
        isNumberOrDecimalPoint(formatStripFormatting(primVal), decimal))
    ) {
      setValue(currentVal);
    }
  };

  const handleBlur = () => {
    let newVal = formatValue(value, decimal, formatType);
    if (maxVal) {
      if (newVal.max && Number(unformat(newVal.max)) > maxVal) {
        newVal.max = maxVal.toString();
      }
      if (newVal.min && Number(unformat(newVal.min)) > maxVal) {
        newVal.min = maxVal.toString();
      }
      newVal = formatValue(newVal, decimal, formatType);
    }
    if (minVal) {
      if (newVal.max && Number(unformat(newVal.max)) < minVal) {
        newVal.max = minVal.toString();
      }
      if (newVal.min && Number(unformat(newVal.min)) < minVal) {
        newVal.min = minVal.toString();
      }
      newVal = formatValue(newVal, decimal, formatType);
    }

    setValue(newVal);
    const isValid = checkValidity();
    if (onBlur && isValid) {
      onBlur(formatOrUndefined(formatStripFormatting, newVal));
    }
  };

  const setToNum = (type: RangeTypes) => {
    const cleanValue = formatOrUndefined(formatStripFormatting, value);
    if (!undefinedNullOrEmptyString(value?.[type])) {
      const newVal = { ...cleanValue, [type]: `${Number(cleanValue?.[type])}` };
      setValue(newVal);
    }
  };

  return (
    <>
      <div className={classNames(styles.inputContainer, className)}>
        <div className={styles.InputCell}>
          {!hideLabels && <label>Min</label>}
          <div className={styles.InputWrapper}>
            <Input
              theme={{
                InputElement: classNames(styles.Input, {
                  [styles.InputWithPercent]: showPercentSymbol,
                }),
              }}
              disabled={disabled}
              dataHcName={`${dataHcName}-min-input`}
              value={value?.min || ''}
              placeholder={placeHolders?.min}
              onFocus={() => {
                checkValidity();
                setValue({
                  min: formatStripFormatting(value?.min || ''),
                  max: value?.max,
                });
                if (formatType === 'DECIMAL_NUMBER') {
                  setToNum('min');
                }
              }}
              onChange={(newVal: string) =>
                handleChange({
                  min: newVal,
                })
              }
              onBlur={handleBlur}
            />
            {showPercentSymbol && (
              <span className={styles.PercentSymbol}>%</span>
            )}
          </div>
          {subjectDisplay && (
            <div
              data-hc-name={`${dataHcName}-relative-value`}
              className={styles.RelativeValue}
            >
              {subjectDisplay.min}
            </div>
          )}
        </div>
        <div className={styles.InputCell}>
          {!hideLabels && <label>Max</label>}
          <div className={styles.InputWrapper}>
            <Input
              theme={{
                InputElement: classNames(styles.Input, {
                  [styles.InputWithPercent]: showPercentSymbol,
                }),
              }}
              disabled={disabled}
              dataHcName={`${dataHcName}-max-output`}
              placeholder={placeHolders?.max}
              value={value.max !== undefined ? `${value.max}` : ''}
              onChange={(newVal: string) =>
                handleChange({
                  max: newVal,
                })
              }
              onFocus={() => {
                checkValidity();
                setValue({
                  min: value?.min,
                  max: formatStripFormatting(value?.max || ''),
                });
                if (formatType === 'DECIMAL_NUMBER') {
                  setToNum('max');
                }
              }}
              onBlur={handleBlur}
            />
            {showPercentSymbol && (
              <span className={styles.PercentSymbol}>%</span>
            )}
          </div>
          {subjectDisplay && (
            <div className={styles.RelativeValue}>{subjectDisplay.max}</div>
          )}
        </div>
      </div>
      {error && (
        <div
          data-hc-name={`${dataHcName}-error`}
          className={styles.rangeInputError}
        >
          {error}
        </div>
      )}
    </>
  );
};
